import moment from 'moment'
import 'moment-timezone'

// Short relative time config
moment.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 's',
    ss: '%ds',
    m: '1m',
    mm: '%dm',
    h: '1h',
    hh: '%dh',
    d: '1d',
    dd: '%dd',
    M: '1M',
    MM: '%dM',
    y: '1y',
    yy: '%dy',
  },
})

moment.relativeTimeThreshold('s', 59) // Seconds up to 59s
moment.relativeTimeThreshold('m', 59) // Minutes up to 59m
moment.relativeTimeThreshold('h', 24) // Hours up to 24h

export function todaysDate() {
  return moment().tz('America/New_York').format('dddd, MMMM Do')
}

// Current date key in the newsroom timezone, e.g. "2026-07-12".
export function todaysDateKey() {
  return moment().tz('America/New_York').format('YYYY-MM-DD')
}

// Short label for the edition navigator, e.g. "Fri, Jul 10".
export function editionDayLabel(dateKey) {
  return moment(dateKey, 'YYYY-MM-DD').format('ddd, MMM D')
}

// Masthead label for an edition day, e.g. "Sunday, July 20th" — the homepage
// header must name the EDITION being shown, not the clock (pre-5am the site
// still serves yesterday's edition).
export function editionDayFull(dateKey) {
  return moment(dateKey, 'YYYY-MM-DD').format('dddd, MMMM Do')
}

// The day an edition COVERS, e.g. "Thursday, August 6th" — an edition
// published Friday morning reports Thursday's news, and the masthead has to
// say so or the dates on the story links read as a mistake.
export function coverageDayLabel(dateKey) {
  return moment(dateKey, 'YYYY-MM-DD').format('dddd, MMMM Do')
}

// An edition's name by its publish weekday, e.g. "Friday Edition".
export function editionName(dateKey) {
  return `${moment(dateKey, 'YYYY-MM-DD').format('dddd')} Edition`
}

// Full label for edition page titles, e.g. "July 11, 2026".
export function editionFullDate(dateKey) {
  return moment(dateKey, 'YYYY-MM-DD').format('MMMM D, YYYY')
}

// Yesterday's date key in the newsroom timezone.
export function yesterdaysDateKey() {
  return moment().tz('America/New_York').subtract(1, 'day').format('YYYY-MM-DD')
}

// "YYYY/MM/DD" for a story URL, in the newsroom timezone. A story published at
// ~01:00 UTC belongs to the prior Eastern day (the edition day, and the day the
// canonical URL uses), so URLs must be built in ET, not the server's local tz.
export function formatDateForURL(date) {
  return moment.utc(date).tz('America/New_York').format('YYYY/MM/DD')
}

// "YYYY-MM-DD" edition-day key for a timestamp, in the newsroom timezone.
export function easternDateKey(date) {
  return moment.utc(date).tz('America/New_York').format('YYYY-MM-DD')
}

// UTC instant bounds of an Eastern day key — for querying a UTC-stored
// published_date by ET edition day (handles the EDT/EST offset via moment).
export function easternDayStartUtc(dateKey) {
  return moment
    .tz(dateKey, 'YYYY-MM-DD', 'America/New_York')
    .startOf('day')
    .toISOString()
}
export function easternDayEndUtc(dateKey) {
  return moment
    .tz(dateKey, 'YYYY-MM-DD', 'America/New_York')
    .endOf('day')
    .toISOString()
}

// Canonical story/topic URL, in the newsroom timezone. Single source of truth
// for every card, list, and layout that links to a story.
export function storyUrl(story) {
  if (!story?.published_date || !story?.slug || !story?.category) return null
  return `/news/${story.category.toLowerCase()}/${formatDateForURL(
    story.published_date
  )}/${story.slug}`
}

// An article's published time as the site writes it in lists: hours for the
// last day ("3h"), the Eastern date before that ("Oct 9"), with the exact
// time for a hover title ("Oct 9, 4:12 PM ET"). Times come from the outlets
// (some only via their feed), so minutes are never shown on the page.
// Server pages pass `now` once per render so the label can't change between
// the HTML and hydration.
export function articleTimeLabel(date, now = Date.now()) {
  if (!date) return null
  const t = moment.utc(date)
  if (!t.isValid()) return null
  const et = t.clone().tz('America/New_York')
  const hours = (now - t.valueOf()) / 3600000
  return {
    label:
      hours < 24 ? `${Math.max(1, Math.floor(hours))}h` : et.format('MMM D'),
    title: et.format('MMM D, h:mm A [ET]'),
  }
}

export function formatToEST(dateString) {
  const dateInEST = moment.utc(dateString).tz('America/New_York')
  return {
    long: dateInEST.format('MMM Do, YYYY, h:mm A [ET]'),
    short: dateInEST.format('h:mm A [ET]'),
  }
}

export function convertUnixToEasternTime(unixTimestamp) {
  return moment
    .unix(unixTimestamp)
    .tz('America/New_York')
    .format('MMMM D, YYYY h:mm A z')
}

// Sunday→Saturday week in the newsroom timezone.
export function getCurrentWeek() {
  const start = moment().tz('America/New_York').startOf('week')
  return {
    startOfWeek: start.format('YYYY-MM-DD'),
    endOfWeek: start.clone().add(6, 'days').format('YYYY-MM-DD'),
  }
}

export function getLastWeek() {
  const start = moment()
    .tz('America/New_York')
    .startOf('week')
    .subtract(7, 'days')
  return {
    startOfLastWeek: start.format('YYYY-MM-DD'),
    endOfLastWeek: start.clone().add(6, 'days').format('YYYY-MM-DD'),
  }
}

export function formatDate(date) {
  return moment(date).format('YYYY-MM-DD')
}

// When a story page last changed: the latest of its publish time, its
// updated_date and its both-sides page's last rewrite (story_format
// .generated_at, written by the pipeline on every rewrite). Search engines
// read it as dateModified / article:modified_time; updated_date alone never
// moved, so every page claimed it hadn't changed since it ran (2026-10-21).
export function storyModifiedDate(story) {
  const times = [
    story?.published_date,
    story?.updated_date,
    story?.story_format?.generated_at,
  ].filter(Boolean)
  if (!times.length) return undefined
  return times.reduce((a, b) => (Date.parse(b) > Date.parse(a) ? b : a))
}
